import { AxiosResponse } from 'axios';
import axiosServices from '../utils/axios';
import { QueryMethod } from './utils';
import type { BaseError, FetchParams, RequestConfig, ValidationError } from './types';

export * from './types'

const API_URL = process.env.REACT_APP_API_URL || 'https://product.velomatch.dev';
const GOGETA_API_V1_PREFIX = '/gogeta-api/v1';

export async function fetch<TResponse, TData = unknown>(
  path: string,
  method: QueryMethod,
  data?: TData,
  params?: FetchParams,
  config?: RequestConfig<TData>,
): Promise<TResponse> {
  try {
    const response: AxiosResponse<TResponse, ValidationError | BaseError> = await axiosServices({
      url: API_URL + path,
      method,
      data,
      params,
      ...config,
    });

    return response.data;

  } catch (error) {
    console.error(error);
    throw error;
  }
}

const get = <TResponse>(path: string, params?: FetchParams, config?: RequestConfig) =>
  fetch<TResponse>(path, QueryMethod.GET, undefined, params, config);

const post = <TResponse, TData = unknown>(
  path: string,
  data?: TData,
  params?: FetchParams,
  config?: RequestConfig<TData>,
) => fetch<TResponse, TData>(path, QueryMethod.POST, data, params, config);

const put = <TResponse, TData = unknown>(
  path: string,
  data?: TData,
  params?: FetchParams,
  config?: RequestConfig<TData>,
) => fetch<TResponse, TData>(path, QueryMethod.PUT, data, params, config);

const patch = <TResponse, TData = unknown>(
  path: string,
  data?: TData,
  params?: FetchParams,
  config?: RequestConfig<TData>,
) => fetch<TResponse, TData>(path, QueryMethod.PATCH, data, params, config);

const deleteRequest = <TResponse, TData = unknown>(
  path: string,
  data?: TData,
  params?: FetchParams,
  config?: RequestConfig<TData>,
) => fetch<TResponse, TData>(path, QueryMethod.DELETE, data, params, config);

const joinPath = (basePath: string, path = '') => {
  const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

  if (!path) {
    return normalizedBasePath;
  }

  return path.startsWith('/') ? `${normalizedBasePath}${path}` : `${normalizedBasePath}/${path}`;
};

export const createApi = (basePath: string) => ({
  get: <TResponse>(path = '', params?: FetchParams, config?: RequestConfig) =>
    get<TResponse>(joinPath(basePath, path), params, config),
  post: <TResponse, TData = unknown>(path = '', data?: TData, params?: FetchParams, config?: RequestConfig<TData>) =>
    post<TResponse, TData>(joinPath(basePath, path), data, params, config),
  put: <TResponse, TData = unknown>(path = '', data?: TData, params?: FetchParams, config?: RequestConfig<TData>) =>
    put<TResponse, TData>(joinPath(basePath, path), data, params, config),
  patch: <TResponse, TData = unknown>(path = '', data?: TData, params?: FetchParams, config?: RequestConfig<TData>) =>
    patch<TResponse, TData>(joinPath(basePath, path), data, params, config),
  delete: <TResponse, TData = unknown>(path = '', data?: TData, params?: FetchParams, config?: RequestConfig<TData>) =>
    deleteRequest<TResponse, TData>(joinPath(basePath, path), data, params, config),
});

export const createGogetaApi = (path: string) => createApi(joinPath(GOGETA_API_V1_PREFIX, path));

export const authApi = createGogetaApi('auth');
export const confirmEmailApi = createGogetaApi('confirm-email');
export const tenantApi = createGogetaApi('tenant');
export const usersApi = createGogetaApi('users');
export const userDetailsApi = createGogetaApi('user-details');
export const userEmployeeSettingsApi = createGogetaApi('user-employee-settings');
export const usersSettingsApi = createGogetaApi('users-settings');
export const exportCsvApi = createGogetaApi('export-csv');
export const filesApi = createGogetaApi('files');
export const retailerApi = createGogetaApi('retailer');
export const accountingApi = createGogetaApi('accounting');
export const invoicesApi = createGogetaApi('invoices');
export const notificationApi = createGogetaApi('notification');
export const notificationMarkedApi = createGogetaApi('notification-marked');
export const payrollApi = createGogetaApi('payroll');
export const personApi = createGogetaApi('person');
export const refundsApi = createGogetaApi('refunds');
export const shopifyApi = createGogetaApi('shopify');
export const voucherApi = createGogetaApi('voucher');
export const flyerApi = createGogetaApi('flyer');
export const b2bFormApi = createGogetaApi('b2b-form');
